import React, { useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { OutlineCard, Switch, useToast } from '@morressier/alembic';
import { useUpdateJournalSettings } from 'src/api/mutations';
import { FieldLabel } from 'src/components/Field';

interface FeatureFlagsTabProps {
  journalId: string;
  journalName: string;
  isUADEnable?: boolean;
  enableConsolidatedPdf?: boolean;
}

type FeatureFlagKey = 'isUADEnable' | 'assignReviewers' | 'enableConsolidatedPdf';

interface FeatureFlagSwitchProps {
  label: string;
  subText: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const FeatureFlagSwitch: React.FC<FeatureFlagSwitchProps> = ({
  label,
  subText,
  checked,
  onChange
}) => (
  <OutlineCard borderless paddingX={4} paddingY={6}>
    <Flex alignItems="center" justifyContent="space-between">
      <FieldLabel label={label} subText={subText} isRequired={false} />
      <Switch
        isChecked={checked}
        onChange={event => onChange(event.target.checked)}
      />
    </Flex>
  </OutlineCard>
);

const FeatureFlagsTab: React.FC<FeatureFlagsTabProps> = ({
  journalId,
  isUADEnable,
  enableConsolidatedPdf
}) => {
  const toast = useToast();
  const updateSettingMutation = useUpdateJournalSettings(journalId);

  const [isUADEnabled, setIsUADEnabled] = useState(!!isUADEnable);
  const [, setIsConsolidatedPdfEnabled] = useState(
    !!enableConsolidatedPdf
  );

  const handleToggle = async (
    key: FeatureFlagKey,
    label: string,
    checked: boolean
  ) => {
    if (key === 'isUADEnable') setIsUADEnabled(checked);
    if (key === 'enableConsolidatedPdf') setIsConsolidatedPdfEnabled(checked);

    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: { [key]: checked }
    } as any);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: `${label} ${checked ? 'enabled' : 'disabled'}`,
        isClosable: true
      });
      return;
    }

    // Revert on failure
    if (key === 'isUADEnable') setIsUADEnabled(!checked);
    if (key === 'enableConsolidatedPdf') setIsConsolidatedPdfEnabled(!checked);
    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string' ? res.message : 'Something went wrong',
      isClosable: true
    });
  };

  return (
    <Flex flexDirection="column" gap={4} width="100%">
      <FeatureFlagSwitch
        label="Anomaly Detection"
        subText="Enable anomaly detection for submissions."
        checked={isUADEnabled}
        onChange={checked =>
          handleToggle('isUADEnable', 'Anomaly Detection', checked)
        }
      />     
    </Flex>
  );
};

export default React.memo(FeatureFlagsTab);
